import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';

const Wrap = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
  padding: 1.5rem;
  font-family: 'Inter', 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const Card = styled.div`
  max-width: 420px;
  width: 100%;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  text-align: center;
`;

const Title = styled.h1`
  margin: 0 0 0.5rem;
  font-size: 1.125rem;
  color: #043663;
`;

const Msg = styled.p`
  margin: 0;
  color: #4b5563;
  font-size: 0.9375rem;
  line-height: 1.5;
`;

const HANDOFF_KEY = 'hps_support_session_handoff';

function readHashParams() {
  const raw = typeof window !== 'undefined' ? window.location.hash.replace(/^#/, '') : '';
  const params = new URLSearchParams(raw);
  return {
    token: params.get('token') || '',
    next: params.get('next') || '/player/tournaments',
  };
}

function stashHandoff(token, next) {
  try {
    sessionStorage.setItem(HANDOFF_KEY, JSON.stringify({ token, next }));
  } catch (_) {
    /* ignore quota / private mode */
  }
}

function readStashedHandoff() {
  try {
    const raw = sessionStorage.getItem(HANDOFF_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.token) {
      return {
        token: String(parsed.token),
        next: parsed.next || '/player/tournaments',
      };
    }
  } catch (_) {
    /* ignore */
  }
  return null;
}

function clearStashedHandoff() {
  try {
    sessionStorage.removeItem(HANDOFF_KEY);
  } catch (_) {
    /* ignore */
  }
}

function readHandoff() {
  const fromHash = readHashParams();
  if (fromHash.token) {
    stashHandoff(fromHash.token, fromHash.next);
    if (window.history?.replaceState) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    return fromHash;
  }
  return readStashedHandoff() || { token: '', next: '/player/tournaments' };
}

/**
 * One-time handoff for super-admin "View as player".
 * Reads JWT from URL hash, stores player token, clears hash, redirects.
 */
export default function PlayerSupportSessionPage() {
  const { acceptSupportSessionToken } = useAuth();
  const navigate = useNavigate();
  const [message, setMessage] = useState('Starting support session…');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { token, next } = readHandoff();
      if (!token) {
        if (!cancelled) setMessage('Missing support session token.');
        return;
      }
      const result = await acceptSupportSessionToken(token);
      if (cancelled) return;
      if (!result.success) {
        clearStashedHandoff();
        setMessage(result.message || 'Could not start support session.');
        return;
      }
      clearStashedHandoff();
      const dest =
        typeof next === 'string' && next.startsWith('/player')
          ? next
          : result.redirectUrl || '/player/tournaments';
      navigate(dest, { replace: true });
    })();
    return () => {
      cancelled = true;
    };
    // Run once on mount for the hash handoff.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Wrap>
      <Card>
        <Title>Support session</Title>
        <Msg>{message}</Msg>
      </Card>
    </Wrap>
  );
}
